import { ensureDefined, voidFunction } from '@avantia/client-and-server-utilities';
import { createControlId } from '@avantia/questionset-model';
import { useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { triggerModalActionEvent } from '../../actions/eventActions';
import { HpOnClickEvent } from '../../types';
import {
  HpModalWidgetProps,
  HpModalWidgetStateProps,
  HpShowConfirmModalFunction,
  HpShowInformationModalFunction,
  HpShowModalWidgetProps
} from '../../types/modalWidgetTypes';
import { createHandlers, createInitialState } from './modalWidgetFunctions';

let showModal: (props: HpShowModalWidgetProps) => void = voidFunction;

export const showConfirmModal: HpShowConfirmModalFunction = (props) => showModal(props);
export const showInformationModal: HpShowInformationModalFunction = (props) => showModal(props);

export function ModalWidget({ pageProps, questionSet }: HpModalWidgetProps): JSX.Element | null {
  const [state, setState] = useState<HpModalWidgetStateProps>(createInitialState());
  const confirmOnClickHandler = useRef<HpOnClickEvent | null>(null);
  const cancelOnClickHandler = useRef<HpOnClickEvent | null>(null);

  const {
    showModal: showModalFunc,
    handleCancel,
    handleConfirm,
    modalId
  } = createHandlers({
    setState,
    pageProps,
    questionSet,
    confirmOnClickHandler,
    cancelOnClickHandler,
    getState: () => state,
    triggerModalActionEvent,
    getHtmlElement: (selector) => ensureDefined(jQuery(selector)[0], 'synthetic target'),
    showConfirmModal,
    showInformationModal
  });

  showModal = showModalFunc;

  if (!(handleCancel && handleConfirm)) {
    return null;
  }

  const {
    customButtons,
    id,
    modalBodyClass,
    modalClass,
    modalCopyText,
    modalFooterClass,
    modalHeaderClass,
    question,
    show
  } = state;
  if (!question || !id || !modalCopyText) {
    return null;
  }

  const { body, cancelButtonStyle, cancelButtonText, confirmButtonStyle, confirmButtonText, title } = modalCopyText;

  let buttons: JSX.Element;
  if (customButtons) {
    buttons = customButtons({ handleConfirm, handleCancel });
  } else {
    buttons = (
      <>
        {confirmButtonText !== null && (
          <button
            id={createControlId({ questionId: id })}
            className={`btn btn-${confirmButtonStyle || 'primary'} animated`}
            name={`hp-group-modal-confirm-${id}`}
            onClick={handleConfirm}
            type="button"
          >
            {confirmButtonText || 'Confirm'}
          </button>
        )}
        {cancelButtonText !== null && (
          <button
            id={createControlId({ questionId: `${id}-cancel` })}
            className={`btn btn-${cancelButtonStyle || 'secondary'} animated`}
            name={`hp-group-modal-close-${id}`}
            onClick={handleCancel}
            type="button"
          >
            {cancelButtonText || 'Cancel'}
          </button>
        )}
      </>
    );
  }

  return (
    <Modal id={modalId} className={modalClass || 'hp-modal-dialog'} show={show} onHide={handleCancel}>
      <Modal.Header className={modalHeaderClass} closeButton>
        {title && <Modal.Title>{title}</Modal.Title>}
      </Modal.Header>
      <Modal.Body className={modalBodyClass}>{body}</Modal.Body>
      <Modal.Footer className={modalFooterClass}>{buttons}</Modal.Footer>
    </Modal>
  );
}
